import {differenceInHours, parseISO, startOfDay} from 'date-fns';
import {FormControl, FormField, useFormContext} from 'platform/components';
import {Box, Grid, GridItem, Heading, Space, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {indexBy, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {RentalOrderResponseBody, useGetRentalOrderQuery} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {MileageInput, openPredefinedNotesDropdown, useVatRatesOptions} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, useRequiredParams} from 'shared/web';

import {FUEL_TANK_STEPS} from '../../../../../constants/FUEL_TANK_STEPS';
import {RentalOrderNodeContext} from '../../../../../constants/RentalOrderNodeContext';
import {useGetHandleUpdateChargedKilometers} from '../hooks/useGetHandleUpdateChargedKilometers';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';
import {ConditionInformationRow} from './ConditionInformationRow';

interface ConditionWhenRentingProps extends RequiredTestIdProps {
  control: FormControl<ReturnVehicleFormValues>;
  formApi: UseFormReturn<ReturnVehicleFormValues>;
  rentalOrder: RentalOrderResponseBody | Nullish;
}

export function ConditionWhenReturning(props: ConditionWhenRentingProps) {
  const {id: rentalOrderId} = useRequiredParams();
  const {data: rentalOrder} = useGetRentalOrderQuery({rentalOrderId});
  const {setValues} = useFormContext<ReturnVehicleFormValues>();
  const [vatRatesOptions] = useVatRatesOptions();
  const vatRatesByCode = indexBy((vatRate) => vatRate.value, vatRatesOptions);

  const vehicleId = rentalOrder?.rentalVehicleId;
  const shouldValidateMileage = isNilOrEmpty(rentalOrder?.returnVehicle?.actualMileage);

  const {
    data: equipmentData,
    isLoading: isLoadingEquipment,
    isError: isEquipmentError,
  } = useReadCodelistQuery({codelistId: 'equipment'});

  const {
    data: rentTypeData,
    isLoading: isLoadingRentType,
    isError: isRentTypeError,
  } = useReadCodelistQuery({codelistId: 'rentType'});

  const rentTypeOptions =
    rentTypeData?.codes?.map((code) => ({
      value: code.codeId,
      label: code.name,
    })) || [];

  const equipmentOptions =
    equipmentData?.codes?.map((code) => ({
      value: code.codeId,
      label: code.name,
    })) || [];

  const conditionNote = useWatch({control: props.control, name: 'conditionNote'});

  const [handleUpdateChargedKilometers] = useGetHandleUpdateChargedKilometers({
    formApi: props.formApi,
    rentalOrder: props.rentalOrder,
  });

  const handleChangePaidDays = (value: Date | null) => {
    const plannedRentalPeriodFrom = props.rentalOrder?.plannedRentalPeriod?.from;

    const [freeDailyKilometers, actualMileage, dailyRate, rentalPriceVatType] =
      props.formApi.getValues([
        'rentPrice.freeDailyKilometers',
        'actualMileage',
        'rentPrice.dailyRate',
        'rentPrice.rentalPrice.vatType',
      ]);

    const rentalPriceVatRate = Number(vatRatesByCode?.[rentalPriceVatType ?? '']?.rate) || 0;

    if (isNil(value) || isNil(plannedRentalPeriodFrom)) {
      return;
    }

    const to = value;
    const from = parseISO(plannedRentalPeriodFrom);

    const diffHours = differenceInHours(to, from);
    const paidDays = Math.ceil(diffHours / 24);

    const totalFreeKilometers = (freeDailyKilometers ?? 0) * paidDays;
    const rentalPrice = (dailyRate ?? 0) * paidDays;
    const rentalPriceWithVat = rentalPrice * (1 + rentalPriceVatRate / 100);
    const rentalPriceVat = rentalPriceWithVat - rentalPrice;

    setValues({
      'rentPrice.paidDays': paidDays,
      'rentPrice.totalFreeKilometers': totalFreeKilometers,
      'rentPrice.rentalPrice.withoutVat': rentalPrice,
      'rentPrice.rentalPrice.withVat': rentalPriceWithVat,
      'rentPrice.rentalPrice.vat': rentalPriceVat,
    });

    handleUpdateChargedKilometers(actualMileage, totalFreeKilometers);
  };

  const handleUpdateActualMileage = (value: number | null) => {
    const totalFreeKilometers = props.formApi.getValues('rentPrice.totalFreeKilometers');
    handleUpdateChargedKilometers(value, totalFreeKilometers);
  };

  return (
    <VStack>
      <Heading size={4}>{i18n.t('page.rentalOrder.labels.conditionWhenReturning')}</Heading>
      <Space vertical={4} />

      <Grid columns={4}>
        <GridItem span={4}>
          <Grid columns={4}>
            <FormField
              control={props.control}
              type="choice"
              isNotClearable
              isDisabled={isRentTypeError}
              isLoading={isLoadingRentType}
              name="rentType"
              isRequired
              options={rentTypeOptions}
              label={i18n.t('page.rentalOrder.labels.rentType')}
              data-testid={suffixTestId('rentType', props)}
            />
          </Grid>
        </GridItem>

        <ConditionInformationRow control={props.control} rentalOrder={props.rentalOrder} />

        <FormField
          control={props.control}
          type="dateTime"
          name="actualReturnDate"
          minDate={
            props.rentalOrder?.plannedRentalPeriod?.from
              ? startOfDay(parseISO(props.rentalOrder?.plannedRentalPeriod?.from))
              : undefined
          }
          onChange={handleChangePaidDays}
          isRequired
          label={i18n.t('page.rentalOrder.labels.actualReturnDate')}
          data-testid={suffixTestId('actualMileage', props)}
        />

        <MileageInput
          control={props.control}
          formApi={props.formApi}
          vehicleId={vehicleId}
          name="actualMileage"
          isRequired
          shouldShowWarning={shouldValidateMileage}
          shouldShowDelta={shouldValidateMileage}
          label={i18n.t('page.rentalOrder.labels.actualMileage')}
          onChange={handleUpdateActualMileage}
          data-testid={suffixTestId('actualMileage', props)}
        />

        <FormField
          control={props.control}
          type="slider"
          name="fuelTank"
          isRequired
          min={0}
          max={1}
          step={0.05}
          nonlinearTicks={FUEL_TANK_STEPS}
          label={i18n.t('page.rentalOrder.labels.fuelTank')}
          data-testid={suffixTestId('fuelTank', props)}
        />
      </Grid>

      <VStack spacing={4}>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="multiChoice"
            name="equipment"
            isDisabled={isEquipmentError}
            isLoading={isLoadingEquipment}
            options={equipmentOptions}
            label={i18n.t('page.rentalOrder.labels.equipment')}
            data-testid={suffixTestId('equipment', props)}
          />
        </Box>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="textarea"
            name="conditionNote"
            label={i18n.t('page.rentalOrder.labels.conditionNote')}
            data-testid={suffixTestId('conditionNote', props)}
            labelActions={[
              {
                title: i18n.t('general.labels.templates'),
                leftIcon: 'content/add_circle',
                onClick: (event) => {
                  openPredefinedNotesDropdown(event, {
                    note: conditionNote ?? null,
                    resource: EntityResourceIds.rentalOrder,
                    context: RentalOrderNodeContext.returnVehicleConditionNote,
                    onPrefill: (text) => {
                      props.formApi.setValue('conditionNote', text);
                    },
                    'data-testid': suffixTestId('conditionNoteTemplates', props),
                  });
                },
              },
            ]}
          />
        </Box>
      </VStack>
    </VStack>
  );
}
